import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy } from "lucide-react";
import { getUniversityTheme } from "@/lib/universityThemes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MEDALS = ["#FFD700", "#C0C0C0", "#CD7F32"];
const COUNTRY_FLAGS = {
  Georgia: "ge", Hungary: "hu", Italy: "it", Jordan: "jo", Moldova: "md",
  Palestine: "ps", Poland: "pl", Romania: "ro", "Saudi Arabia": "sa",
  Syria: "sy", "Türkiye": "tr", UAE: "ae",
};

export const UniversityScoreboard = ({ compact = false, top = 0, minimal = false }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [pops, setPops] = useState({});
  const prevRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${API}/university-scores`);
        if (!r.ok) return;
        const d = await r.json();
        const next = d.universities || [];
        let base = prevRef.current;
        if (!base) {
          try { base = JSON.parse(localStorage.getItem("cm_uni_points") || "{}"); } catch { base = {}; }
        }
        const inc = {};
        next.forEach((u) => {
          const old = base[u.external_id];
          if (old != null && u.points > old) inc[u.external_id] = u.points - old;
        });
        prevRef.current = Object.fromEntries(next.map((u) => [u.external_id, u.points]));
        localStorage.setItem("cm_uni_points", JSON.stringify(prevRef.current));
        setRows(next);
        if (Object.keys(inc).length) {
          setPops(inc);
          setTimeout(() => setPops({}), 4000);
        }
      } catch {}
    };
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, []);

  if (!rows.length) return null;
  const max = Math.max(...rows.map((r) => r.points), 1);

  if (minimal) {
    const MEDAL_BG = ["bg-amber-400", "bg-slate-300", "bg-orange-300"];
    return (
      <div data-testid="top-universities-minimal">
        <h3 className="text-base font-bold text-[#0F2744] dark:text-white mb-2">Top Universities</h3>
        <ul className="space-y-2">
          {rows.slice(0, top || 5).map((u, i) => (
            <li key={u.external_id}>
              <button onClick={() => navigate(`/university/${u.external_id}`)}
                      className="w-full flex items-center gap-2 text-left hover:opacity-80"
                      data-testid={`top-uni-row-${u.external_id}`}>
                <span className={`flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold text-white ${MEDAL_BG[i] || "bg-slate-400"}`}>{i + 1}</span>
                <span className="flex-1 text-sm font-medium text-[#0F2744] dark:text-white truncate">{u.name}</span>
                <span className="text-xs font-bold text-slate-500 shrink-0">{u.points} pts</span>
              </button>
            </li>
          ))}
        </ul>
        <button onClick={() => navigate("/championship")} className="mt-3 text-sm font-semibold text-[#2563EB] flex items-center gap-0.5" data-testid="view-full-ranking-btn">
          View full ranking →
        </button>
      </div>
    );
  }

  return (
    <section className={compact ? "w-full max-w-6xl mb-8" : "py-16 lg:py-20 bg-secondary/30"} data-testid="university-scoreboard">
      <div className={compact ? "" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"}>
        <div className={compact ? "text-center mb-4" : "text-center mb-10"}>
          <h2 className={compact ? "text-xl font-bold mb-1" : "text-3xl sm:text-4xl font-bold mb-3"}>University Points</h2>
          <p className={`text-muted-foreground max-w-2xl mx-auto ${compact ? "text-sm" : ""}`}>
            Every university starts with 10 points — each correct exam answer earns one more. Represent your university!
          </p>
        </div>
        <div className="overflow-x-auto pb-4">
          <div className="flex items-end gap-1.5 min-w-max px-2" data-testid="scoreboard-columns">
            {rows.map((u, i) => {
              const theme = getUniversityTheme(u.external_id);
              const color = theme?.primaryColor || "#1E3A5F";
              const flag = COUNTRY_FLAGS[u.country];
              const h = 32 + Math.round((u.points / max) * 100);
              return (
                <button
                  key={u.external_id}
                  onClick={() => navigate(`/university/${u.external_id}`)}
                  className="relative flex flex-col items-center w-14 group cursor-pointer"
                  data-testid={`scoreboard-column-${u.external_id}`}
                  title={`${u.name} (${u.country}) — ${u.points} points`}
                >
                  {pops[u.external_id] && (
                    <span className="uni-point-pop" data-testid={`scoreboard-pop-${u.external_id}`}>
                      +{pops[u.external_id]}
                    </span>
                  )}
                  {i < 3 && <Trophy className="h-3 w-3 mb-0.5" style={{ color: MEDALS[i] }} />}
                  <span className="text-xs font-bold mb-0.5" data-testid={`scoreboard-points-${u.external_id}`}>
                    {u.points}
                  </span>
                  <div
                    className={`w-9 rounded-t-md border border-border/60 shadow-sm transition-all duration-300 group-hover:opacity-90 group-hover:scale-y-105 origin-bottom ${pops[u.external_id] ? "uni-column-celebrate" : ""}`}
                    style={{
                      height: h,
                      background: flag
                        ? `url(https://flagcdn.com/h240/${flag}.png) center no-repeat`
                        : color,
                      backgroundSize: flag ? "100% 100%" : undefined,
                    }}
                  />
                  <span className="mt-1 text-[9px] leading-tight text-center text-muted-foreground line-clamp-2 group-hover:text-foreground transition-colors">
                    {u.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default UniversityScoreboard;
